import {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { CartItem } from '../types';

interface CartContextValue {
  items: CartItem[];
  itemCount: number;
  subtotal: number;
  addItem: (item: CartItem) => void;
  updateQuantity: (productId: string, size: string, quantity: number) => void;
  removeItem: (productId: string, size: string) => void;
  clear: () => void;
}

const STORAGE_KEY = 'handcrafted.cart';
// Per-line cap. Bigger orders should go through the appointment flow
// (the seller wants to verify stock + arrange a bulk handover offline).
const MAX_QUANTITY = 10;

const CartContext = createContext<CartContextValue | undefined>(undefined);

function loadFromStorage(): CartItem[] {
  if (typeof window === 'undefined') return [];
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (i): i is CartItem =>
        i &&
        typeof i.productId === 'string' &&
        typeof i.title === 'string' &&
        typeof i.imageUrl === 'string' &&
        typeof i.size === 'string' &&
        typeof i.unitPrice === 'number' &&
        typeof i.quantity === 'number' &&
        i.quantity > 0,
    );
  } catch {
    return [];
  }
}

function persistToStorage(items: CartItem[]): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  } catch {
    // localStorage can throw in Safari private mode / full quota — fail silent,
    // cart still works in-memory for the session.
  }
}

export function CartProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<CartItem[]>(() => loadFromStorage());

  useEffect(() => {
    persistToStorage(items);
  }, [items]);

  const addItem = useCallback((incoming: CartItem) => {
    setItems((prev) => {
      const idx = prev.findIndex(
        (i) => i.productId === incoming.productId && i.size === incoming.size,
      );
      if (idx === -1) {
        const qty = Math.min(Math.max(1, incoming.quantity), MAX_QUANTITY);
        return [...prev, { ...incoming, quantity: qty }];
      }
      const merged = [...prev];
      const next = merged[idx].quantity + incoming.quantity;
      merged[idx] = { ...merged[idx], quantity: Math.min(next, MAX_QUANTITY) };
      return merged;
    });
  }, []);

  const updateQuantity = useCallback((productId: string, size: string, quantity: number) => {
    setItems((prev) => {
      if (quantity <= 0) {
        return prev.filter((i) => !(i.productId === productId && i.size === size));
      }
      return prev.map((i) =>
        i.productId === productId && i.size === size
          ? { ...i, quantity: Math.min(quantity, MAX_QUANTITY) }
          : i,
      );
    });
  }, []);

  const removeItem = useCallback((productId: string, size: string) => {
    setItems((prev) => prev.filter((i) => !(i.productId === productId && i.size === size)));
  }, []);

  const clear = useCallback(() => setItems([]), []);

  const itemCount = useMemo(
    () => items.reduce((acc, i) => acc + i.quantity, 0),
    [items],
  );

  const subtotal = useMemo(
    () => items.reduce((acc, i) => acc + i.quantity * i.unitPrice, 0),
    [items],
  );

  const value = useMemo<CartContextValue>(
    () => ({ items, itemCount, subtotal, addItem, updateQuantity, removeItem, clear }),
    [items, itemCount, subtotal, addItem, updateQuantity, removeItem, clear],
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart(): CartContextValue {
  const ctx = useContext(CartContext);
  if (!ctx) {
    throw new Error('useCart must be used inside CartProvider');
  }
  return ctx;
}
